import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { StripeProvider } from '../../../contexts/StripeContext'
import { StripePaymentForm } from '../../CompanyManagement/components/billing/StripePaymentForm'
import { createOnboardingSetupIntent, startOnboardingTrial } from '../../../services/OnboardingBillingService'

/**
 * The company creator saves a card and the 30-day trial starts (SC-438). Nothing is charged
 * today; the card is charged when the trial ends unless it is cancelled. There is no way past
 * this step without a saved card: the cloud keeps the creator here until the trial exists.
 */
export default function CardStep({
  companyId,
  onTrialStarted,
}: {
  companyId: string
  onTrialStarted: () => void
}) {
  const [setupIntent, setSetupIntent] = useState<{ client_secret: string; id?: string } | null>(null)
  const [error, setError] = useState('')
  // A refusal for a missing permission: trying again cannot change the answer.
  const [refused, setRefused] = useState(false)
  const [starting, setStarting] = useState(false)
  const [attempt, setAttempt] = useState(0)
  /**
   * The SetupIntent Stripe has confirmed. Once set, the card is saved: a failed start-trial is
   * retried with it, and the card form never mounts again on an intent that can't be reused.
   */
  const [savedCardIntentId, setSavedCardIntentId] = useState<string | null>(null)
  const latest = useRef(onTrialStarted)
  latest.current = onTrialStarted

  useEffect(() => {
    let cancelled = false
    setError('')
    setRefused(false)
    createOnboardingSetupIntent(companyId)
      .then(({ clientSecret, setupIntentId }) => {
        if (!cancelled) setSetupIntent({ client_secret: clientSecret, id: setupIntentId })
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return
        // Started in another tab: move on.
        if (err.status === 409) latest.current()
        else {
          setError(err.message)
          setRefused(err.status === 403)
        }
      })
    return () => {
      cancelled = true
    }
  }, [companyId, attempt])

  const handleCardSaved = async (confirmedId?: string) => {
    const setupIntentId = confirmedId || setupIntent?.id
    if (!setupIntentId) {
      setError('The card could not be confirmed. Try again.')
      return
    }
    setSavedCardIntentId(setupIntentId)
    setStarting(true)
    setError('')
    setRefused(false)
    try {
      await startOnboardingTrial(companyId, setupIntentId)
      onTrialStarted()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The trial could not be started. Try again.')
      setRefused((err as { status?: number })?.status === 403)
      setStarting(false)
    }
  }

  return (
    <Box data-testid="onboarding-card-step">
      <Typography
        component="span"
        sx={{
          display: 'inline-block',
          mb: 1.25,
          px: 1,
          py: 0.25,
          borderRadius: 5,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.07em',
          bgcolor: 'action.selected',
          color: 'primary.main',
        }}
      >
        30 DAYS FREE
      </Typography>
      <Typography variant="h5" component="h1" fontWeight={700}>
        Start your 30-day free trial
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Full access for your whole team. <b>We won’t charge you today.</b>
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            refused ? undefined : savedCardIntentId ? (
              <Button color="inherit" size="small" onClick={() => void handleCardSaved(savedCardIntentId)}>
                Try again
              </Button>
            ) : !setupIntent ? (
              <Button color="inherit" size="small" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}

      {starting ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
          <CircularProgress size={22} />
          <Typography>Starting your trial…</Typography>
        </Box>
      ) : savedCardIntentId ? null : setupIntent ? (
        <StripeProvider>
          <StripePaymentForm
            setupIntent={setupIntent}
            hideCancel
            hideLink
            submitLabel="Start free trial"
            onSuccess={(id) => void handleCardSaved(id)}
            onError={setError}
          />
        </StripeProvider>
      ) : (
        !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} aria-label="Preparing the card form" />
          </Box>
        )
      )}

      <Box
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
        }}
      >
        <Typography fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockOutlinedIcon fontSize="small" aria-hidden /> No charge until your trial ends (day 30)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Cancel anytime — we’ll email you 3 days before.
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
        Secured by Stripe · card saved now, charged only after your trial
      </Typography>
    </Box>
  )
}
